import React, { useState, useContext, FormEvent, useEffect, useCallback } from "react";
import { Modal, Form, Row, Col, Button, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { DraggableModalDialog } from './DraggableModalDialog';
import { useHttpUtils } from '../Utilities/useHttpUtils';
import useAlert from '../Alerts/useAlert';
import { LotDetailContext } from "../Contexts/LotDetailContext";
import CommentDialog from './CommentDialog';
import { convertLocalTimeZoneToUTC, convertToDateTime, convertUTCToLocalTime } from "../Utilities/useDateUtils";
import DateTimePicker from "../Components/DateTimePicker";
import { DateTime } from "luxon";

export interface SetLotTimingDialogProps {
    showDialog: boolean,
    closeDialog: () => void,
}

export interface LotBuildTimeDTO {
    data: {
        buildStartUtc: string,
        buildEndUtc: string
    },
    comment: string
}

export default function EditLotBuildTimeDialog({ showDialog, closeDialog }: SetLotTimingDialogProps) {
    const { setAlert } = useAlert();
    const { fetchDataAuthenticated } = useHttpUtils();
    const { t } = useTranslation();
    const { lotDetail: { summary: { lotNumber, buildStartUtc, buildEndUtc } }, getLotDetail } = useContext(LotDetailContext);

    const [initialBuildStart, setInitialBuildStart] = useState<DateTime | null>(buildStartUtc && convertUTCToLocalTime(buildStartUtc));
    const [initialBuildEnd, setInitialBuildEnd] = useState<DateTime | null>(buildEndUtc && convertUTCToLocalTime(buildEndUtc));
    useEffect(() => {
        if (buildStartUtc) {
            setInitialBuildStart(convertUTCToLocalTime(buildStartUtc));
        }
    }, [buildStartUtc]);
    useEffect(() => {
        if (buildEndUtc) {
            setInitialBuildEnd(convertUTCToLocalTime(buildEndUtc));
        }
    }, [buildEndUtc]);

    const [lotStart, setLotStart] = useState<DateTime | null>(initialBuildStart);
    const [lotEnd, setLotEnd] = useState<DateTime | null>(initialBuildEnd);
    useEffect(() => {
        setLotStart(initialBuildStart);
    }, [initialBuildStart]);
    useEffect(() => {
        setLotEnd(initialBuildEnd);
    }, [initialBuildEnd]);

    const [loading, setLoading] = useState<boolean>(false);
    const [showCommentDialog, setShowCommentDialog] = useState(false);
    const [formValidated, setFormValidated] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>("");

    const handleSave = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        e.stopPropagation();

        const form = e.currentTarget;

        if (form.checkValidity() === true) {
            setShowCommentDialog(true);
        } else {
            console.warn("Lot timing form was invalid");
        }

        setFormValidated(true);
    };

    const closeDialogAndResetForm = useCallback(() => {
        setLotStart(initialBuildStart);
        setLotEnd(initialBuildEnd);
        setFormValidated(false);
        setErrorMessage("");
        closeDialog();
    }, [closeDialog, initialBuildEnd, initialBuildStart]);

    const updateLotTimingWithComment = useCallback(async (comment: string) => {
        let url = `/api/v1/lot/${lotNumber}/lotBuildTime`;
        let body: LotBuildTimeDTO = {
            data: {
                buildStartUtc: convertLocalTimeZoneToUTC(lotStart).toISO() ?? "",
                buildEndUtc: convertLocalTimeZoneToUTC(lotEnd).toISO() ?? ""
            },
            comment: comment
        };

        setLoading(true);
        await fetchDataAuthenticated(url, 'PUT', JSON.stringify(body))
            .then(async response => {
                if (response.ok) {
                    setAlert("success", "Success", `Updated the lot build timing. Recalculating lot`);
                    setErrorMessage("");
                    getLotDetail();
                    closeDialogAndResetForm();
                } else {
                    let errorMessage = await response.text();

                    setErrorMessage(errorMessage);
                }
            })
            .catch(error => {
                setAlert("danger", "Error", error.message);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [closeDialogAndResetForm, fetchDataAuthenticated, getLotDetail, lotEnd, lotNumber, lotStart, setAlert]);

    return (
        <>
            <Modal dialogAs={DraggableModalDialog} show={showDialog} onHide={closeDialogAndResetForm} backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>{t<string>("Dialogs.LotBuildTime.Title")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {
                        loading
                            ?
                            <div className="d-flex justify-content-center mb-2">
                                <Spinner animation="border" />
                            </div>
                            :
                            <Form id="setLotTiming" noValidate validated={formValidated} onSubmit={handleSave}>
                                <Form.Group as={Row} className="mb-2">
                                    <Form.Label column>{t<string>("Dialogs.LotBuildTime.BuildStart")}</Form.Label>
                                    <Col sm={8}>
                                        <DateTimePicker required name="buildStart" dateTime={lotStart ? new Date(lotStart.toISO({ includeOffset: false }) ?? "") : null} handleDateSelect={(date) => date && setLotStart(convertToDateTime(date))} />
                                    </Col>
                                </Form.Group>
                                <Form.Group as={Row} className="mb-2">
                                    <Form.Label column>{t<string>("Dialogs.LotBuildTime.BuildEnd")}</Form.Label>
                                    <Col sm={8}>
                                        <DateTimePicker required name="buildEnd" dateTime={lotEnd ? new Date(lotEnd.toISO({ includeOffset: false }) ?? "") : null} handleDateSelect={(date) => date && setLotEnd(convertToDateTime(date))} />
                                    </Col>
                                </Form.Group>
                                {!loading && errorMessage !== "" &&
                                    <Row>
                                        <p className="text-danger"><FontAwesomeIcon icon={faTriangleExclamation} className="me-1 mt-3" /> {errorMessage}</p>
                                    </Row>
                                }
                            </Form>
                    }
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeDialogAndResetForm} disabled={loading}>
                        {t<string>("Application.Controls.Cancel")}
                    </Button>
                    <Button variant="primary" type="submit" form="setLotTiming" disabled={loading}>
                        {t<string>("Application.Controls.Save")}
                    </Button>
                </Modal.Footer>
            </Modal>
            <CommentDialog showDialog={showCommentDialog} closeDialog={() => setShowCommentDialog(false)} onSave={updateLotTimingWithComment} />
        </>
    );
}
